import React, { useEffect } from "react";
import {
  Activity,
  CheckCircle,
  Circle,
  ArrowRight,
  Play,
  Image as ImageIcon,
} from "lucide-react";

const SafeImage = ({ src, alt, className }) => {
  const [error, setError] = React.useState(false);

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-neutral-200 text-neutral-400 ${className}`}
      >
        <ImageIcon size={32} className="mb-2 opacity-50" />
        <span className="text-xs font-mono uppercase tracking-widest text-center px-4">
          Image Missing:
          <br />
          {alt}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
};

export default function App() {
  useEffect(() => {
    if (!document.getElementById("tailwind-cdn")) {
      const script = document.createElement("script");
      script.id = "tailwind-cdn";
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 font-sans selection:bg-blue-900 selection:text-white">
      {/* Navigation Header */}
      <nav className="border-b border-neutral-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SafeImage
              src="/1000025220.png"
              alt="OrbitX Logo"
              className="h-12 w-auto object-contain"
            />
            <span className="font-bold tracking-widest text-lg uppercase text-slate-900">
              OrbitX Space Innovations
            </span>
          </div>
          <div className="hidden md:flex gap-8 text-sm font-medium text-neutral-500 uppercase tracking-wider">
            <a
              href="#hardware"
              className="hover:text-slate-900 transition-colors"
            >
              Hardware
            </a>
            <a
              href="#roadmap"
              className="hover:text-slate-900 transition-colors"
            >
              Roadmap
            </a>
            <a
              href="#test-footage"
              className="hover:text-slate-900 transition-colors"
            >
              Test Video
            </a>
          </div>
        </div>
      </nav>

      {}
      <header className="relative pt-24 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center gap-16">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-mono uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              Status: Hardware Integration
            </div>

            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              AEGIS-L <br />
              <span className="text-neutral-500 font-light">
                Liquid Rocket Engine
              </span>
            </h1>

            <p className="text-lg text-neutral-600 max-w-lg leading-relaxed">
              Core engine components manufactured. We are currently actively
              routing fluid systems and assembling the V1 test stand
              architecture in preparation for cold flow testing.
            </p>

            <div className="pt-4 flex gap-4">
              <a
                href="#hardware"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-slate-800 transition-colors"
              >
                View Hardware <ArrowRight size={16} />
              </a>
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="bg-white border border-neutral-200 p-4 shadow-sm relative group">
              <div className="absolute top-2 left-2 text-[10px] font-mono text-neutral-400 uppercase">
                Fig 1. Chamber/Nozzle
              </div>
              <SafeImage
                src="/1000024857.png"
                alt="AEGIS-L Nozzle and Combustion Chamber"
                className="w-full h-64 md:h-96 object-cover bg-neutral-100"
              />
            </div>
          </div>
        </div>
      </header>

      {}
      <section
        id="hardware"
        className="py-24 bg-white border-y border-neutral-200"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-16 md:flex justify-between items-end border-b border-neutral-200 pb-6">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                Current Hardware Status
              </h2>
              <p className="text-neutral-500 mt-2 max-w-2xl">
                Visual documentation of completed physical components prior to
                hot-fire integration.
              </p>
            </div>
            <div className="text-sm font-mono text-neutral-400 hidden md:block">
              LAST UPDATED: Q3 2026
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Injector Card */}
            <div className="group border border-neutral-200 bg-neutral-50 flex flex-col">
              <div className="p-4 border-b border-neutral-200 bg-white flex justify-between items-center">
                <span className="font-mono text-sm font-bold text-slate-900">
                  INJECTOR_PLATE_V1
                </span>
                <span className="text-xs font-mono px-2 py-1 bg-green-100 text-green-800">
                  MACHINED
                </span>
              </div>
              <div className="flex-1 p-6 flex items-center justify-center bg-neutral-100 h-[300px]">
                <SafeImage
                  src="/1000024656.jpg"
                  alt="Machined Injector Plate"
                  className="w-full h-full object-cover shadow-sm ring-1 ring-black/5"
                />
              </div>
              <div className="p-4 bg-white text-sm text-neutral-600 border-t border-neutral-200">
                Primary injector element post-machining. Awaiting final fluid
                manifold integration.
              </div>
            </div>

            {/* Test Stand Card */}
            <div className="group border border-neutral-200 bg-neutral-50 flex flex-col">
              <div className="p-4 border-b border-neutral-200 bg-white flex justify-between items-center">
                <span className="font-mono text-sm font-bold text-slate-900">
                  TEST_STAND_FRAME_V1
                </span>
                <span className="text-xs font-mono px-2 py-1 bg-blue-100 text-blue-800">
                  IN ASSEMBLY
                </span>
              </div>
              <div className="flex-1 p-6 flex items-center justify-center bg-neutral-100 h-[300px]">
                <SafeImage
                  src="/1000024655.jpg"
                  alt="Test Stand Frame"
                  className="w-full h-full object-cover shadow-sm ring-1 ring-black/5"
                />
              </div>
              <div className="p-4 bg-white text-sm text-neutral-600 border-t border-neutral-200">
                Initial structural frame constructed. Currently preparing for
                DAQ and safety abort system mounting.
              </div>
            </div>
          </div>
        </div>
      </section>

      {}
      <section id="roadmap" className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-3xl font-bold tracking-tight">
              Development Roadmap
            </h2>
            <p className="text-slate-400 mt-2">
              Our sequential approach to reaching first ignition.
            </p>
          </div>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-700 before:to-transparent">
            {/* Phase 1 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-green-500 text-slate-900 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                <CheckCircle size={18} className="text-white" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-800 border border-slate-700 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">Hardware Manufacturing</h3>
                  <span className="font-mono text-xs text-green-400">
                    COMPLETED
                  </span>
                </div>
                <p className="text-slate-400 text-sm">
                  Finalized CAD. Successfully machined core engine components
                  including the combustion chamber, nozzle, and injector plate.
                </p>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-blue-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                <Activity size={18} className="text-white animate-pulse" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-800 border border-blue-500/30 p-6 relative">
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">Test Stand Integration</h3>
                  <span className="font-mono text-xs text-blue-400">
                    IN PROGRESS
                  </span>
                </div>
                <p className="text-slate-400 text-sm">
                  V1 frame constructed. Currently routing fluid systems, DAQ
                  (Data Acquisition) sensors, and safety abort mechanics.
                </p>
              </div>
            </div>

            {/* Phase 3 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-slate-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                <Circle size={14} className="text-slate-400" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-800/50 border border-slate-700/50 p-6 opacity-75">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-slate-300">
                    Cold Flow Testing
                  </h3>
                  <span className="font-mono text-xs text-slate-500">
                    UPCOMING
                  </span>
                </div>
                <p className="text-slate-500 text-sm">
                  Pressurizing the system with inert fluids to verify injector
                  pressure drops, flow rates, and valve timing.
                </p>
              </div>
            </div>

            {/* Phase 4 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-slate-900 bg-slate-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                <Circle size={14} className="text-slate-400" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-800/50 border border-slate-700/50 p-6 opacity-75">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg text-slate-300">
                    Static Hot Fire
                  </h3>
                  <span className="font-mono text-xs text-slate-500">
                    UPCOMING
                  </span>
                </div>
                <p className="text-slate-500 text-sm">
                  First ignition sequence. Characterizing combustion stability
                  and thermal performance on the stand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {}
      <section id="test-footage" className="py-24 bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12 md:flex justify-between items-end border-b border-neutral-200 pb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-mono uppercase tracking-widest mb-3">
                <Play size={12} className="fill-amber-800" />
                Hot Fire Test Visualization
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                Hot Fire Sequence Video
              </h2>
              <p className="text-neutral-500 mt-2 max-w-2xl">
                Animated visualization demonstrating the target ignition and plume expansion dynamics for our static hot fire test on the V1 architecture.
              </p>
            </div>
            <div className="text-sm font-mono text-neutral-400 hidden md:block">
              FILE: 1000024863.MP4
            </div>
          </div>

          <div className="relative bg-slate-950 rounded-xl overflow-hidden shadow-2xl border border-slate-800">
            <div className="p-3 bg-slate-900 border-b border-slate-800 flex justify-between items-center text-xs font-mono text-slate-400">
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse"></span>
                AEGIS-L SIMULATION PREVIEW
              </span>
              <span>1000024863.MP4</span>
            </div>

            <video
              className="w-full h-auto max-h-[600px] object-contain mx-auto bg-black"
              controls
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="/1000024863.mp4" type="video/mp4" />
              Your browser does not support playing MP4 videos.
            </video>
          </div>
        </div>
      </section>

      {}
      <footer className="bg-slate-950 py-12 border-t border-slate-800 text-center">
        <div className="max-w-6xl mx-auto px-6 flex flex-col items-center">
          <SafeImage
            src="/1000025220.png"
            alt="OrbitX Logo"
            className="h-10 w-auto opacity-50 grayscale mb-6"
          />
          <p className="text-slate-500 text-sm mb-2">
            OrbitX Space Innovations © {new Date().getFullYear()}
          </p>
          <p className="text-slate-600 text-xs font-mono uppercase tracking-widest">
            Inclusive Space Access
          </p>
        </div>
      </footer>
    </div>
  );
}
